import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Activity, Database, Mail, Bell, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

function fmtUptime(sec) {
  if (!sec && sec !== 0) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

const STATUS = {
  ok: { color: "text-green-500", bar: "bg-green-600", label: "V pořádku", Icon: CheckCircle2 },
  warning: { color: "text-yellow-500", bar: "bg-yellow-500", label: "Blíží se limit", Icon: AlertTriangle },
  critical: { color: "text-red-500", bar: "bg-red-600", label: "Kritické — navyš tarif!", Icon: AlertTriangle },
};

function Bar({ label, used, limit, pct, tone }) {
  const t = STATUS[tone] || STATUS.ok;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-neutral-400 mb-1">
        <span>{label}</span>
        <span className={t.color}>{used} / {limit} ({pct}%)</span>
      </div>
      <div className="h-2 bg-white/10 overflow-hidden">
        <div className={`h-full ${t.bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

export function LiveOnlineChip() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/visitors/live").then(({ data }) => { if (alive) setCount(data.count); }).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (count === null) return null;
  return (
    <div data-testid="live-online-chip" className={`flex items-center gap-2 px-3 py-1.5 border text-xs font-head tracking-widest ${count > 0 ? "border-green-700/50 bg-green-950/30 text-green-400" : "border-white/10 bg-[#111] text-neutral-500"}`} title="Návštěvníci právě na webu">
      <span className="relative flex h-2.5 w-2.5">
        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${count > 0 ? "bg-green-500" : "bg-neutral-600"} opacity-60`} />
        <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${count > 0 ? "bg-green-500" : "bg-neutral-600"}`} />
      </span>
      {count} online
    </div>
  );
}

export default function AdminSystemStatus() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/system/status").then(({ data }) => { if (alive) setData(data); }).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!data) return null;
  const em = data.email || {};
  const emStatus = STATUS[em.status] || STATUS.ok;

  return (
    <div className="border border-white/10 bg-[#0d0d0d] p-5 mb-6" data-testid="system-status-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head uppercase text-neutral-200 flex items-center gap-2"><Activity className="w-4 h-4 text-nv" />Stav systému</h3>
        <span className="text-[10px] tracking-widest text-neutral-600 uppercase">obnovuje se každých 30 s</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="border border-white/10 bg-[#111] p-3 flex items-center gap-3" data-testid="status-online">
          <span className="relative flex h-3 w-3"><span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${data.online_visitors > 0 ? "bg-green-500" : "bg-neutral-600"} opacity-60`} /><span className={`relative inline-flex rounded-full h-3 w-3 ${data.online_visitors > 0 ? "bg-green-500" : "bg-neutral-600"}`} /></span>
          <div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Teď na webu</div><div className={`text-sm font-head ${data.online_visitors > 0 ? "text-green-500" : "text-neutral-300"}`}>{data.online_visitors} {data.online_visitors === 1 ? "návštěvník" : "návštěvníků"}</div></div>
        </div>
        <div className="border border-white/10 bg-[#111] p-3 flex items-center gap-3" data-testid="status-db">
          <Database className={`w-5 h-5 ${data.db_ok ? "text-green-500" : "text-red-500"}`} />
          <div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Databáze</div><div className={`text-sm font-head ${data.db_ok ? "text-green-500" : "text-red-500"}`}>{data.db_ok ? "Běží" : "Chyba"}</div></div>
        </div>
        <div className="border border-white/10 bg-[#111] p-3 flex items-center gap-3">
          <Clock className="w-5 h-5 text-neutral-400" />
          <div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Server běží</div><div className="text-sm font-head text-neutral-200">{fmtUptime(data.uptime_seconds)}</div></div>
        </div>
        <div className="border border-white/10 bg-[#111] p-3 flex items-center gap-3">
          <Bell className="w-5 h-5 text-neutral-400" />
          <div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">Push zařízení</div><div className="text-sm font-head text-neutral-200">{data.push_subscriptions}</div></div>
        </div>
        <div className="border border-white/10 bg-[#111] p-3 flex items-center gap-3" data-testid="status-email">
          <Mail className={`w-5 h-5 ${emStatus.color}`} />
          <div><div className="text-[10px] text-neutral-500 uppercase tracking-widest">E-maily</div><div className={`text-sm font-head ${emStatus.color}`}>{emStatus.label}</div></div>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-neutral-400"><Mail className="w-3.5 h-3.5 text-red-600" />E-mailová kvóta {em.resend_live ? <span className="text-green-600 text-[10px]">● Resend aktivní</span> : <span className="text-yellow-600 text-[10px]">● mockováno</span>}</div>
        <Bar label="Dnes" used={em.sent_today} limit={em.daily_limit} pct={em.daily_pct} tone={em.daily_pct >= 90 ? "critical" : em.daily_pct >= 75 ? "warning" : "ok"} />
        <Bar label="Tento měsíc" used={em.sent_month} limit={em.monthly_limit} pct={em.monthly_pct} tone={em.monthly_pct >= 90 ? "critical" : em.monthly_pct >= 75 ? "warning" : "ok"} />
        {em.status === "critical" && (
          <p className="text-[11px] text-red-400 flex items-center gap-1" data-testid="email-critical-warning"><AlertTriangle className="w-3 h-3" />Docházejí e-maily! Navyš tarif na resend.com, jinak přestanou chodit potvrzení a poukazy.</p>
        )}
        {em.status === "warning" && (
          <p className="text-[11px] text-yellow-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Blížíš se limitu e-mailů — zvaž navýšení tarifu.</p>
        )}
      </div>
    </div>
  );
}
